import * as XLSX from 'xlsx';
import {
  VisitasAgendaDiaKey,
  VisitasAgendaDias,
  VisitasAgendaFila,
  emptyVisitasAgendaDias,
} from '../types/visitasAgenda';

export interface VisitasAgendaImportRowError {
  fila: number;
  mensaje: string;
}

export interface VisitasAgendaImportResult {
  filas: Omit<VisitasAgendaFila, 'idVisitasAgenda'>[];
  errores: VisitasAgendaImportRowError[];
}

interface AgendaColumnIndices {
  idxCuenta: number;
  idxRazon: number;
  idxDir: number;
  idxFrecuencia: number;
}

interface AgendaSheetLayout {
  sheetName: string;
  headerRowIndex: number;
  columns: AgendaColumnIndices;
}

const stripAccents = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const normalizeHeader = (value: unknown): string =>
  stripAccents(String(value ?? '').trim().toLowerCase());

const parseDiasFromFrecuencia = (raw: string): VisitasAgendaDias => {
  const dias = emptyVisitasAgendaDias();
  const text = stripAccents(raw.trim()).toUpperCase();

  if (!text) return dias;

  if (text.includes('TODOS')) {
    (Object.keys(dias) as VisitasAgendaDiaKey[]).forEach((k) => {
      dias[k] = true;
    });
    return dias;
  }

  const tokens = text.split(/[/,\-|]+/).map((t) => t.trim()).filter(Boolean);

  const matchToken = (token: string): VisitasAgendaDiaKey | null => {
    if (token.startsWith('LUN')) return 'lun';
    if (token.startsWith('MAR')) return 'mar';
    if (token.startsWith('MIE')) return 'mier';
    if (token.startsWith('JUE')) return 'jue';
    if (token.startsWith('VIE')) return 'vie';
    if (token.startsWith('SAB')) return 'sab';
    if (token.startsWith('DOM')) return 'dom';
    return null;
  };

  tokens.forEach((token) => {
    const key = matchToken(token);
    if (key) dias[key] = true;
  });

  return dias;
};

const isNroCuentaHeader = (header: string): boolean => {
  if (!header) return false;
  if (
    header.includes('tipoformulario') ||
    header.includes('comprobante') ||
    header.includes('formulario') ||
    header.includes('tipo cliente')
  ) {
    return false;
  }
  if (/nro\s*\.?\s*cuenta/.test(header)) return true;
  if (header.includes('nro') && header.includes('cuenta')) return true;
  return header === 'cuenta' || header.startsWith('cuenta ');
};

const isFrecuenciaHeader = (header: string): boolean => header.includes('frecuencia');

const resolveColumnIndices = (headers: string[]): AgendaColumnIndices | null => {
  const idxCuenta = headers.findIndex(isNroCuentaHeader);
  const idxFrecuencia = headers.findIndex(isFrecuenciaHeader);

  if (idxCuenta < 0 || idxFrecuencia < 0) return null;

  const idxRazon = headers.findIndex(
    (h) => (h.includes('razon') && h.includes('social')) || h === 'nombre' || h.startsWith('nombre '),
  );
  const idxDir = headers.findIndex(
    (h) =>
      (h.includes('direccion') && h.includes('envio')) ||
      (h.includes('direccion') && !h.includes('facturacion') && !h.includes('ciudad') && !h.includes('resumen')),
  );

  return {
    idxCuenta,
    idxRazon,
    idxDir,
    idxFrecuencia,
  };
};

const findAgendaSheetLayout = (workbook: XLSX.WorkBook): AgendaSheetLayout | null => {
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;

    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
    }) as unknown[][];

    for (let r = 0; r < Math.min(rows.length, 25); r += 1) {
      const headers = (rows[r] as unknown[]).map(normalizeHeader);
      const columns = resolveColumnIndices(headers);
      if (columns) {
        return { sheetName, headerRowIndex: r, columns };
      }
    }
  }
  return null;
};

const parseNroCliente = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }
  const cleaned = String(value ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n) : NaN;
};

const cellText = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

export const parseVisitasAgendaExcel = async (file: File): Promise<VisitasAgendaImportResult> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });

  if (!workbook.SheetNames.length) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'El archivo no tiene hojas.' }] };
  }

  const layout = findAgendaSheetLayout(workbook);
  if (!layout) {
    const hojas = workbook.SheetNames.join(', ');
    return {
      filas: [],
      errores: [
        {
          fila: 0,
          mensaje: `No se encontró una hoja con columnas "Nro Cuenta" y "FRECUENCIA". Hojas en el archivo: ${hojas}. Use el listado de clientes con agenda (no otro export del sistema).`,
        },
      ],
    };
  }

  const sheet = workbook.Sheets[layout.sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  }) as unknown[][];

  const { headerRowIndex, columns } = layout;
  const { idxCuenta, idxRazon, idxDir, idxFrecuencia } = columns;

  const filas: Omit<VisitasAgendaFila, 'idVisitasAgenda'>[] = [];
  const errores: VisitasAgendaImportRowError[] = [];

  for (let i = headerRowIndex + 1; i < rows.length; i += 1) {
    const row = rows[i] as unknown[];
    if (!row || row.every((c) => cellText(c) === '')) continue;

    const nroCliente = parseNroCliente(row[idxCuenta]);
    if (!Number.isFinite(nroCliente) || nroCliente <= 0) continue;

    const razon = idxRazon >= 0 ? cellText(row[idxRazon]) : '';
    const direccion = idxDir >= 0 ? cellText(row[idxDir]) : '';
    const frecuencia = cellText(row[idxFrecuencia]);
    const dias = parseDiasFromFrecuencia(frecuencia);

    if (!Object.values(dias).some(Boolean)) {
      if (frecuencia) {
        errores.push({ fila: i + 1, mensaje: `Frecuencia no reconocida: "${frecuencia}"` });
      }
      continue;
    }

    const nombreDisplay = razon || direccion || `Cliente ${nroCliente}`;

    filas.push({
      nroCliente,
      codigoDireccion: undefined,
      nombreDisplay,
      dias,
    });
  }

  if (filas.length === 0 && errores.length === 0) {
    return {
      filas: [],
      errores: [
        {
          fila: 0,
          mensaje: `Hoja "${layout.sheetName}": no hay filas válidas. Revise Nro Cuenta y valores en FRECUENCIA.`,
        },
      ],
    };
  }

  return { filas, errores };
};
